import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";

const facts = [
  { k: "Entity", v: "Aero Soft Solutions Pty Ltd", id: "entity" },
  { k: "ABN", v: "147 024 828 80", id: "abn" },
  { k: "ACN", v: "702 482 880", id: "acn" },
  { k: "Registered office", v: "Level 1, 63-67 Ann St, Surry Hills NSW 2010", id: "office" },
];

const pillars = [
  { t: "Speed", d: "Short paths from need to fielded capability." },
  { t: "Precision", d: "Engineering discipline in every deliverable." },
  { t: "Partnership", d: "Working shoulder-to-shoulder with your team." },
];

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="about" ref={ref} className="relative bg-white py-24 lg:py-32" data-testid="about-section">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-12">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="relative h-[460px] overflow-hidden rounded-t-[220px] rounded-b-[28px] bg-sky-100 sm:h-[600px]" data-testid="about-image">
              <motion.img src="/img/above.jpg" alt="Aircraft overhead against a deep blue sky" style={{ y, scale: 1.2 }} className="absolute inset-0 h-full w-full object-cover" />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7 lg:pt-10">
          <Reveal>
            <Eyebrow>02 — About</Eyebrow>
            <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-[-0.03em] text-ink sm:text-5xl">
              Built on one idea: capability needs <span className="font-serif font-normal italic text-sky-600">don't wait.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg" data-testid="about-text">
              Aero Soft Solutions is an Australian aviation solutions company headquartered in Surry Hills, Sydney. We specialise in
              delivering aviation solutions at the speed of capability needs — combining software, engineering and integration know-how to help
              operators move faster with confidence.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-3">
            {pillars.map((p, i) => (
              <Reveal key={p.t} delay={0.1 + i * 0.08}>
                <div className="h-full rounded-3xl border border-sky-100 bg-[#F4F9FD] p-6 transition-colors duration-300 hover:border-sky-300" data-testid={`about-pillar-${p.t.toLowerCase()}`}>
                  <p className="font-serif text-3xl italic text-sky-600">{p.t}</p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <dl className="mt-12 divide-y divide-slate-200 border-y border-slate-200" data-testid="about-facts">
              {facts.map((f) => (
                <div key={f.id} className="grid grid-cols-3 gap-4 py-4" data-testid={`about-fact-${f.id}`}>
                  <dt className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">{f.k}</dt>
                  <dd className="col-span-2 text-sm font-medium text-ink sm:text-base">{f.v}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
